import { inject, Injectable } from "@angular/core";
import { map, Observable, Subject } from "rxjs";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import {
	AddressBookWalletHostModel,
	AddressBookWalletPayload,
	AddressBookWalletModel,
	WalletHostsResponse,
	AddressBookWalletTransactionData,
	AddressBookWalletHostType,
	DigitalSignatureProof,
	WalletVerificationData
} from "../model/address-book-wallet.model";
import { Fee } from "@modules/main/components/step-summary/sending-modal/entities/fee.model";
import { filter } from "rxjs/operators";
import { SendTransaction } from "@models/transaction";
import { StatsigClientService } from "@core-services/statsig/statsig.service";

@Injectable({
	providedIn: "root"
})
export class AddressBookWalletService {
	private readonly statsigClientService = inject(StatsigClientService);
	private readonly addressBookUrl: string = "user/address-book";
	private readonly walletHostsUrl: string = "user/address-book/wallet-hosts";
	private readonly paymentsTransactionsUrl: string = "payments/transactions";

	public shouldRefreshWallets: Subject<boolean> = new Subject<boolean>();

	public constructor(private apiCallService: ApicallService) {}

	public addNewWallet(newWallet: AddressBookWalletPayload): Observable<AddressBookWalletModel> {
		return this.apiCallService.post(this.addressBookUrl, new ApicallOption(null, newWallet));
	}

	public editLegacyWallet(walletId: string, newWallet: AddressBookWalletPayload): Observable<AddressBookWalletModel> {
		return this.apiCallService.patch(`${this.addressBookUrl}/${walletId}`, new ApicallOption(null, newWallet));
	}

	public getWallets(): Observable<AddressBookWalletModel[]> {
		const option: ApicallOption = new ApicallOption();
		option.apiVersion = "v2";
		return this.apiCallService
			.get(this.addressBookUrl, option)
			.pipe(
				map((list) =>
					list.filter(
						(entry: AddressBookWalletModel) =>
							entry.wallet_host_type !== AddressBookWalletHostType.SMART_WALLET
					)
				)
			);
	}

	public getWalletHosts(): Observable<AddressBookWalletHostModel[]> {
		const option: ApicallOption = new ApicallOption();
		option.raw = true;
		return this.apiCallService
			.get(this.walletHostsUrl, option)
			.pipe(map(({ data: { all_wallet_hosts } }: WalletHostsResponse) => all_wallet_hosts));
	}

	public deleteWallet(walletId: string): Observable<undefined> {
		return this.apiCallService.deleteRequest(`${this.addressBookUrl}/${walletId}`);
	}

	public validateContactAndGetFee(walletId: string, transaction: SendTransaction): Observable<Fee> {
		return this.apiCallService
			.post("wallet/" + walletId + "/send/validate", new ApicallOption(Fee, transaction))
			.pipe(
				map((fees: Fee[]) => fees[0]),
				filter((fee) => !!fee)
			);
	}

	public getPaymentTransaction$(transactionId: string): Observable<AddressBookWalletTransactionData> {
		return this.apiCallService.get(`${this.paymentsTransactionsUrl}/${transactionId}`);
	}

	public getWalletVerificationData(addressBookId: string, transactionId: string): Observable<WalletVerificationData> {
		const option: ApicallOption = new ApicallOption();
		option.apiVersion = "v2";
		option.params = { transaction_id: transactionId };
		return this.apiCallService.get(`${this.addressBookUrl}/${addressBookId}/verification-results`, option);
	}

	public sendVerificationProof(addressBookId: string, proof: DigitalSignatureProof): Observable<void> {
		const option: ApicallOption = new ApicallOption(null, { proof });
		option.apiVersion = "v2";
		option.hasNullResponse = true;
		return this.apiCallService.post(`${this.addressBookUrl}/${addressBookId}/verification-results`, option);
	}
}
